import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useAgency } from './context/AgencyContext';
import AdminLayout from './components/AdminLayout';
import LoginPage from './pages/LoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ClientsPage from './pages/ClientsPage';
import AddClientPage from './pages/AddClientPage';
import ClientDetailPage from './pages/ClientDetailPage';
import AgencyOnboardingPage from './pages/AgencyOnboardingPage';
// Import existing pages for sub-views
import CallsPage from './pages/CallsPage';
import CallDetailPage from './pages/CallDetailPage';
import LeadsPage from './pages/LeadsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import UsageDashboardPage from './pages/UsageDashboardPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AgencySettingsPage from './pages/AgencySettingsPage';
import BillingPage from './pages/BillingPage';
import ClientBillingPage from './pages/ClientBillingPage';
import CheckoutReturnPage from './pages/CheckoutReturnPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Wrapper that redirects to onboarding if user has no agency
function RequireAgency({ children }: { children: React.ReactNode }) {
  const { agencies, loading } = useAgency();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If user has no agencies, redirect to onboarding
  if (agencies.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Agency onboarding - for new users without an agency */}
      <Route path="/onboarding" element={
        <PrivateRoute>
          <AgencyOnboardingPage />
        </PrivateRoute>
      } />
      {/* Main app routes - require agency */}
      <Route path="/" element={
        <PrivateRoute>
          <RequireAgency>
            <AdminLayout />
          </RequireAgency>
        </PrivateRoute>
      }>
        <Route index element={<AdminDashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<AddClientPage />} />
        {/* Client detail routes */}
        <Route path="clients/:businessId" element={<ClientDetailPage />} />
        <Route path="clients/:businessId/settings" element={<ClientDetailPage />} />
        <Route path="clients/:businessId/calls" element={<CallsPage />} />
        <Route path="clients/:businessId/calls/:callId" element={<CallDetailPage />} />
        <Route path="clients/:businessId/leads" element={<LeadsPage />} />
        <Route path="clients/:businessId/appointments" element={<AppointmentsPage />} />
        {/* Agency pages */}
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<AgencySettingsPage />} />
        {/* Usage & Billing */}
        <Route path="usage" element={<UsageDashboardPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="checkout/return" element={<CheckoutReturnPage />} />
        <Route path="client-billing" element={<ClientBillingPage />} />
        {/* Super Admin - Platform-wide visibility */}
        <Route path="platform" element={<SuperAdminPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
